import {
  AccountApiFormData,
  AccountFormData,
  AccountFormSubmissionResponse,
  AccountTokenValidationApiResponse,
  Canton,
  CompanyType,
  Country,
  Industry,
  Language,
  SelectOption,
  TokenValidationResponse,
} from '@/types';
import { apiService } from './api';

/**
 * Account Form Service
 *
 * FOR DEVELOPMENT: Uses hardcoded data until backend API is implemented
 * All methods simulate API behavior with realistic delays and responses
 */
export class AccountFormService {
  /**
   * Validates a token for account form access
   * Public endpoint - no authentication required
   *
   * Uses the Account-specific validation endpoint: GET /api/v1/account/validate-token/{token}
   */
  async validateToken(token: string): Promise<TokenValidationResponse> {
    try {
      const response = await apiService.get<AccountTokenValidationApiResponse>(
        `/account/validate-token/${token}`
      );

      // The API returns { success: true, message: "Token is valid", result: { valid: true } }
      if (response.data?.success && response.data?.result?.valid) {
        return {
          valid: true,
          message: response.data.message || 'Token is valid',
          expires_at: response.data.result.expires_at || undefined,
        };
      }

      throw new Error(response.data?.message || 'Token validation failed');
    } catch (error: unknown) {
      // Handle API errors (401 for invalid tokens, 500 for server errors)
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: {
            status?: number;
            data?: { message?: string };
          };
        };

        if (apiError.response?.status === 401) {
          throw new Error(
            apiError.response.data?.message || 'Token is invalid or expired'
          );
        }

        // Re-throw other API errors with message
        throw new Error(
          apiError.response?.data?.message || 'Token validation failed'
        );
      }

      // Handle network or other errors
      throw new Error('Network error during token validation');
    }
  }

  /**
   * Submits account form data
   * Public endpoint - uses token for validation
   *
   * Real API implementation using POST /api/v1/account/submit-form
   */
  async submitAccountForm(
    token: string,
    data: AccountFormData
  ): Promise<AccountFormSubmissionResponse> {
    try {
      const response = await apiService.post('/account/submit-form', {
        token,
        ...data,
      });
      return response.data as AccountFormSubmissionResponse;
    } catch (error: unknown) {
      // Handle API errors
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: {
            data?: {
              message?: string;
              errors?: Record<string, string[]>;
            };
            status?: number;
          };
        };

        // Handle validation errors (422)
        if (
          apiError.response?.status === 422 &&
          apiError.response?.data?.errors
        ) {
          const validationError = new Error('Validation failed');
          (
            validationError as unknown as { errors: Record<string, string[]> }
          ).errors = apiError.response.data.errors;
          throw validationError;
        }

        // Handle invalid token (401)
        if (apiError.response?.status === 401) {
          throw new Error(
            apiError.response?.data?.message || 'Invalid or expired token'
          );
        }

        throw new Error(
          apiError.response?.data?.message || 'Form submission failed'
        );
      }
      throw new Error('Network error during form submission');
    }
  }

  /**
   * Submits account form data using API format
   * Accepts data in the format expected by the API
   */
  async submitAccountFormApi(
    apiFormData: AccountApiFormData
  ): Promise<AccountFormSubmissionResponse> {
    try {
      const response = await apiService.post(
        '/account/submit-form',
        apiFormData
      );
      return response.data as AccountFormSubmissionResponse;
    } catch (error: unknown) {
      // Handle API errors
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as {
          response?: {
            data?: {
              message?: string;
              errors?: Record<string, string[]>;
            };
            status?: number;
          };
        };

        // Handle validation errors (422)
        if (
          apiError.response?.status === 422 &&
          apiError.response?.data?.errors
        ) {
          const validationError = new Error('Validation failed');
          (
            validationError as unknown as { errors: Record<string, string[]> }
          ).errors = apiError.response.data.errors;
          throw validationError;
        }

        // Handle invalid token (401)
        if (apiError.response?.status === 401) {
          throw new Error(
            apiError.response?.data?.message || 'Invalid or expired token'
          );
        }

        throw new Error(
          apiError.response?.data?.message || 'Form submission failed'
        );
      }
      throw new Error('Network error during form submission');
    }
  }

  /**
   * Gets form field options from the backend
   * Public endpoint - no authentication required
   *
   * FOR DEVELOPMENT: Uses hardcoded data until backend API is implemented
   */
  async getFormOptions(): Promise<{
    companyTypes: CompanyType[];
    languages: Language[];
    countries: Country[];
    industries: Industry[];
    cantons: Canton[];
  }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 300));

    return {
      companyTypes: [
        { value: 'einzelunternehmen', label: 'Sole Proprietorship' },
        { value: 'einfache_gesellschaft', label: 'Simple Partnership' },
        { value: 'kollektivgesellschaft', label: 'General Partnership' },
        { value: 'ag', label: 'Stock Corporation (AG)' },
        { value: 'gmbh', label: 'Limited Liability Company (GmbH)' },
        {
          value: 'oeffentlich_rechtliche_institution',
          label: 'Public Institution',
        },
        {
          value: 'kommanditaktiengesellschaft',
          label: 'Partnership Limited by Shares',
        },
        { value: 'kommanditgesellschaft', label: 'Limited Partnership' },
        { value: 'verein', label: 'Association' },
        { value: 'stiftung', label: 'Foundation' },
        { value: 'genossenschaft', label: 'Cooperative' },
        { value: 'other', label: 'Other' },
      ],
      languages: [
        { value: 'de', label: 'Deutsch' },
        { value: 'fr', label: 'Français' },
        { value: 'it', label: 'Italiano' },
        { value: 'en', label: 'English' },
      ],
      countries: [
        { value: 'switzerland', label: 'Switzerland' },
        { value: 'germany', label: 'Germany' },
        { value: 'austria', label: 'Austria' },
        { value: 'france', label: 'France' },
        { value: 'italy', label: 'Italy' },
        { value: 'united_kingdom', label: 'United Kingdom' },
        { value: 'united_states', label: 'United States' },
        { value: 'china', label: 'China' },
        { value: 'india', label: 'India' },
        { value: 'japan', label: 'Japan' },
        { value: 'brazil', label: 'Brazil' },
        { value: 'south_africa', label: 'South Africa' },
        { value: 'australia', label: 'Australia' },
        { value: 'canada', label: 'Canada' },
        { value: 'argentina', label: 'Argentina' },
        { value: 'mexico', label: 'Mexico' },
        { value: 'spain', label: 'Spain' },
        { value: 'sweden', label: 'Sweden' },
        { value: 'norway', label: 'Norway' },
        { value: 'finland', label: 'Finland' },
        { value: 'denmark', label: 'Denmark' },
        { value: 'netherlands', label: 'Netherlands' },
        { value: 'belgium', label: 'Belgium' },
        { value: 'luxembourg', label: 'Luxembourg' },
        { value: 'poland', label: 'Poland' },
        { value: 'czechia', label: 'Czechia' },
        { value: 'hungary', label: 'Hungary' },
        { value: 'turkey', label: 'Turkey' },
        { value: 'russia', label: 'Russia' },
        { value: 'ukraine', label: 'Ukraine' },
        { value: 'others', label: 'Others' },
      ],
      industries: [
        { value: 'automotive', label: 'Automotive' },
        { value: 'banking_finance', label: 'Banken & Finanzdienstleistungen' },
        { value: 'construction_real_estate', label: 'Bau & Immobilien' },
        { value: 'consulting', label: 'Beratung' },
        { value: 'education_science', label: 'Bildung & Wissenschaft' },
        { value: 'energy_environment', label: 'Energie & Umwelt' },
        { value: 'healthcare', label: 'Gesundheitswesen' },
        { value: 'trade', label: 'Handel' },
        { value: 'industry_manufacturing', label: 'Industrie & Fertigung' },
        { value: 'it_software', label: 'IT & Software' },
        { value: 'arts_culture', label: 'Kunst & Kultur' },
        { value: 'agriculture', label: 'Landwirtschaft' },
        { value: 'logistics_transport', label: 'Logistik & Transport' },
        { value: 'media_communication', label: 'Medien & Kommunikation' },
        { value: 'non_profit', label: 'Non-Profit' },
        { value: 'public_service', label: 'Öffentlicher Dienst' },
        { value: 'pharma_biotech', label: 'Pharma & Biotechnologie' },
        { value: 'legal', label: 'Recht' },
        { value: 'sports_leisure', label: 'Sport & Freizeit' },
        { value: 'tourism_gastronomy', label: 'Tourismus & Gastronomie' },
      ],
      cantons: [
        { value: 'ZH', label: 'Zürich' },
        { value: 'BE', label: 'Bern' },
        { value: 'LU', label: 'Luzern' },
        { value: 'GE', label: 'Genf' },
        { value: 'BS', label: 'Basel-Stadt' },
        { value: 'VD', label: 'Waadt' },
        { value: 'AG', label: 'Aargau' },
        { value: 'SG', label: 'St. Gallen' },
      ],
    };
  }

  /**
   * Gets postal codes for a specific country
   * Public endpoint - no authentication required
   *
   * FOR DEVELOPMENT: Uses hardcoded data until backend API is implemented
   */
  async getPostalCodes(country: string): Promise<SelectOption[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 200));

    const postalCodesByCountry: Record<string, SelectOption[]> = {
      CH: [
        { value: '8001', label: '8001 - Zurich' },
        { value: '3000', label: '3000 - Bern' },
        { value: '1200', label: '1200 - Geneva' },
        { value: '4001', label: '4001 - Basel' },
        { value: '1000', label: '1000 - Lausanne' },
      ],
      DE: [
        { value: '10115', label: '10115 - Berlin' },
        { value: '80331', label: '80331 - Munich' },
        { value: '20095', label: '20095 - Hamburg' },
        { value: '50667', label: '50667 - Cologne' },
        { value: '60311', label: '60311 - Frankfurt' },
      ],
      AT: [
        { value: '1010', label: '1010 - Vienna' },
        { value: '5020', label: '5020 - Salzburg' },
        { value: '6020', label: '6020 - Innsbruck' },
        { value: '8010', label: '8010 - Graz' },
      ],
      FR: [
        { value: '75001', label: '75001 - Paris' },
        { value: '69001', label: '69001 - Lyon' },
        { value: '13001', label: '13001 - Marseille' },
      ],
    };

    return postalCodesByCountry[country] || [];
  }

  /**
   * Gets cities for a specific postal code and country
   * Public endpoint - no authentication required
   *
   * FOR DEVELOPMENT: Uses hardcoded data until backend API is implemented
   */
  async getCities(
    country: string,
    postalCode: string
  ): Promise<SelectOption[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 200));

    const citiesByCountryAndPostal: Record<
      string,
      Record<string, SelectOption[]>
    > = {
      CH: {
        '8001': [{ value: 'zurich', label: 'Zurich' }],
        '3000': [{ value: 'bern', label: 'Bern' }],
        '1200': [{ value: 'geneva', label: 'Geneva' }],
        '4001': [{ value: 'basel', label: 'Basel' }],
        '1000': [{ value: 'lausanne', label: 'Lausanne' }],
      },
      DE: {
        '10115': [{ value: 'berlin', label: 'Berlin' }],
        '80331': [{ value: 'munich', label: 'Munich' }],
        '20095': [{ value: 'hamburg', label: 'Hamburg' }],
        '50667': [{ value: 'cologne', label: 'Cologne' }],
        '60311': [{ value: 'frankfurt', label: 'Frankfurt' }],
      },
      AT: {
        '1010': [{ value: 'vienna', label: 'Vienna' }],
        '5020': [{ value: 'salzburg', label: 'Salzburg' }],
        '6020': [{ value: 'innsbruck', label: 'Innsbruck' }],
        '8010': [{ value: 'graz', label: 'Graz' }],
      },
      FR: {
        '75001': [{ value: 'paris', label: 'Paris' }],
        '69001': [{ value: 'lyon', label: 'Lyon' }],
        '13001': [{ value: 'marseille', label: 'Marseille' }],
      },
    };

    return citiesByCountryAndPostal[country]?.[postalCode] || [];
  }

  /**
   * Gets existing account data for pre-filling the form
   * Public endpoint - uses token for identification
   *
   * FOR DEVELOPMENT: Returns null for empty form until backend API is implemented
   */
  async getAccountData(token: string): Promise<AccountFormData | null> {
    try {
      // Validate token first
      const validTokens = [
        'valid-token-123',
        'demo-token-456',
        'test-token-789',
        'account-form-token',
        'dev-token-2025',
      ];

      if (!validTokens.includes(token)) {
        return null;
      }

      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      // Always return null for empty form - no pre-filled data
      return null;
    } catch {
      // Return null if no existing data found
      return null;
    }
  }

  /**
   * Gets available currencies for bank connections
   * Public endpoint - no authentication required
   *
   * FOR DEVELOPMENT: Uses hardcoded data until backend API is implemented
   */
  async getCurrencies(): Promise<SelectOption[]> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 100));

    return [
      { value: 'CHF', label: 'CHF - Schweizer Franken' },
      { value: 'EUR', label: 'EUR - Euro' },
      { value: 'USD', label: 'USD - US Dollar' },
      { value: 'GBP', label: 'GBP - Britisches Pfund' },
      { value: 'JPY', label: 'JPY - Japanischer Yen' },
      { value: 'CAD', label: 'CAD - Kanadischer Dollar' },
      { value: 'AUD', label: 'AUD - Australischer Dollar' },
      { value: 'SEK', label: 'SEK - Schwedische Krone' },
      { value: 'NOK', label: 'NOK - Norwegische Krone' },
      { value: 'DKK', label: 'DKK - Dänische Krone' },
      { value: 'PLN', label: 'PLN - Polnischer Zloty' },
      { value: 'CZK', label: 'CZK - Tschechische Krone' },
      { value: 'HUF', label: 'HUF - Ungarischer Forint' },
    ];
  }
}

// Export singleton instance
export const accountFormService = new AccountFormService();
